import { ChangeDetectorRef, Pipe, PipeTransform } from '@angular/core';
import { map, take } from 'rxjs/operators';

import { GetActiveChannelQuery } from '../../common/generated-types';
import { CurrencyService } from '../../core/providers/currency/currency.service';
import { DataService } from '../../core/providers/data/data.service';

import { GET_ACTIVE_CHANNEL } from './get-active-channel.graphql';
import { GET_EXCHANGE_RATE } from './get-exchange-rate.graphql';

let channelDataPromise: Promise<any> | undefined;
let exchangeRatePromise: Promise<number> | undefined;

export function resetFormatPriceCache(): void {
    channelDataPromise = undefined;
    exchangeRatePromise = undefined;
}

@Pipe({
    name: 'formatPrice',
    pure: false,
})
export class FormatPricePipe implements PipeTransform {

    private latestValue: any = null;
    private latestReturnedValue: any = null;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private dataService: DataService,
        private currencyService: CurrencyService,
    ) {}

    transform(value: number) {
        if (this.latestValue !== value) {
            this.latestValue = value;
            this.formatCurrency(value);
        }
        return this.latestReturnedValue;
    }

    private formatCurrency(value: number) {
        Promise.all([this.getActiveChannel(), this.getExchangeRate()])
            .then(([channel, rate]) => {
                const currency = this.currencyService.currentCurrency;
                if (currency === 'SYP') {
                    if (rate <= 0) {
                        console.warn('[FormatPricePipe] SYP selected but exchange rate is 0 — set a rate in the admin Exchange Rate page.');
                    }
                    const syp = rate > 0 ? (value / 100) * rate : (value / 100);
                    const formatter = Intl.NumberFormat(channel.defaultLanguageCode, {
                        style: 'currency',
                        currency: 'SYP',
                        maximumFractionDigits: 0,
                    });
                    this.latestReturnedValue = formatter.format(syp);
                } else {
                    const formatter = Intl.NumberFormat(channel.defaultLanguageCode, {
                        style: 'currency',
                        currency: channel.currencyCode,
                    });
                    this.latestReturnedValue = formatter.format(value / 100);
                }
                this.changeDetector.markForCheck();
            });
    }

    private getActiveChannel(): Promise<GetActiveChannelQuery['activeChannel']> {
        if (!channelDataPromise) {
            channelDataPromise = this.dataService.query<GetActiveChannelQuery>(GET_ACTIVE_CHANNEL).pipe(
                take(1),
                map(data => data.activeChannel),
            ).toPromise();
        }
        return channelDataPromise;
    }

    private getExchangeRate(): Promise<number> {
        if (!exchangeRatePromise) {
            exchangeRatePromise = this.dataService.query<{ exchangeRate: number }>(GET_EXCHANGE_RATE).pipe(
                take(1),
                map(data => Number(data?.exchangeRate) || 0),
            ).toPromise()
                .then(rate => rate ?? 0)
                .catch(err => {
                    console.warn('[FormatPricePipe] exchangeRate query failed:', err);
                    return 0;
                });
        }
        return exchangeRatePromise;
    }
}
